import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserAuthService } from './auth/userAuth.service';
import { Router } from '@angular/router';
import { HospitalStateService } from './services/hospitalstate.service';
import { IpdFeatureService } from './services/ipd-feature.service';
import { EncryptionService } from './services/encryption.service';
import { inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LoginService {
  private _authenticated: boolean = false;
  private _userResult: any = null;

  private _httpclient = inject(HttpClient);
  private _userService = inject(UserAuthService);
  private _router = inject(Router);
  private _hospital = inject(HospitalStateService);
  private _ipdFeature = inject(IpdFeatureService);
  private _encryption = inject(EncryptionService);

  constructor() { }

  // Access token is now handled by HttpOnly cookies.
  // JS no longer has access to it.
  get accessToken(): string {
    return "";
  }

  private getDecodedToken(): any | null {
    return this._userResult;
  }

  signIn(credentials: { username: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      this._router.navigate(['/dashboard']);
      // return throwError('User is already logged in.');
    }

    return this._encryptAndLogin(credentials).pipe(
      catchError(() => {
        // If decryption failed on server (e.g. key rotated), refetch key and retry once
        this._encryption.clearPublicKey();
        return this._encryptAndLogin(credentials);
      })
    );
  }

  private _encryptAndLogin(credentials: { username: string; password: string }): Observable<any> {
    return from(this._encryption.fetchServerKeyAndTime()).pipe(
      switchMap(() => from(this._encryption.encryptPassword(credentials.password))),
      switchMap((encryptedPassword) => {
        const payload = { userName: credentials.username, password: encryptedPassword };
        return this._httpclient.post(`${environment.baseURl}/Auth/login`, payload, { withCredentials: true });
      }),
      switchMap((response: any) => {
        this._userResult = response.result;
        this._authenticated = true;

        this._userService.user = response.result.userName;

        const hospitalId = this.getHospitalId() as number;
        return this._userService.getHospital(hospitalId).pipe(
          tap(res => {
            this._hospital.setHospital(res.result);
          }),
          map(() => response)
        );
      })
    );
  }

  clearSession(): void {
    this._userResult = null;
    this._authenticated = false;
  }

  isIPDTabShown(): boolean {
    return this._ipdFeature.isEnabled;
  }

  initializeAuth() {
    // debugger;
    return firstValueFrom(
      this._httpclient.get(`${environment.baseURl}/Auth/me`, { withCredentials: true }).pipe(
        tap((response: any) => {
          this._userResult = response.result;
          this._authenticated = true;
          this._userService.user = response.result.userName;
        }),
        switchMap((response: any) => {
          const hospitalId = response.result.hospitalId;
          if (hospitalId) {
            return this._userService.getHospital(hospitalId).pipe(
              tap(res => {
                this._hospital.setHospital(res.result);
              }),
              catchError(() => of(null))
            );
          }
          return of(null);
        }),
        catchError(() => {
          this._authenticated = false;
          return of(null);
        })
      )
    );
  }

  getHospitalFromToken(): string[] | null {
    if (!this._userResult) return null;
    return this._userResult.hospitalId ? [this._userResult.hospitalId.toString()] : [];
  }

  getHospitalId(): number | null {
    return this._userResult?.hospitalId ? Number(this._userResult.hospitalId) : null;
  }

  getUserRoleFromToken(): string[] | null {
    return this._userResult?.roles;
  }

  isDoctor(): boolean {
    const roles = this.getUserRoleFromToken();
    return roles?.includes("Doctor") ?? false;
  }

  getUserId(): number | null {
    return this._userResult?.id ? Number(this._userResult.id) : null;
  }
}